import { useState, useEffect, useRef } from 'react';

import { FlightTicketApiService } from 'Services/ApiServices';
import { getEnvironmentVariablesHandler } from 'Handlers/EnvHandler';
import F2WCustomLogger from 'Services/F2WCustomLogger';

// Keep-alive interval: 50 seconds. Must stay well below the server-side lock
// expiry (LOCK_TIMEOUT_MINUTES = 5 min) so an active editor keeps renewing the
// lock without ever letting it lapse, while a lock left behind by a crashed
// session frees up within ~5 minutes for the next user.
const KEEP_ALIVE_INTERVAL_MS = 50 * 1000;
// Session timeout: show reminder at 55 min, force redirect at 60 min
const SESSION_REMINDER_MS = 55 * 60 * 1000;
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;

export const useTicketLock = (ticketId, user, ticketStatus, userRoles, requiredRole) => {
  const [lockInfo, setLockInfo] = useState(null);
  const [showBlockedPopup, setShowBlockedPopup] = useState(false);
  const [showReminderPopup, setShowReminderPopup] = useState(false);
  const [lockError, setLockError] = useState(null);
  const lockTimeoutRef = useRef();
  const reminderTimeoutRef = useRef();
  const keepAliveIntervalRef = useRef();
  const ownsLockRef = useRef(false);

  const isLockRequired =
    Boolean(ticketId) && ticketStatus?.toLowerCase() === 'pendingbtt' && userRoles?.includes(requiredRole);

  const releaseLock = async () => {
    if (!ownsLockRef.current) return;

    // Clear ownership before the request so beforeunload + unmount cannot release twice.
    ownsLockRef.current = false;
    try {
      await FlightTicketApiService.releaseLock(ticketId, user?.email);
      setLockInfo(null);
    } catch (e) {
      if (e?.response?.status === 409) {
        // Cleanup raced with expiry/reassignment; the current session no longer
        // owns the lock, so there is nothing else it should release.
        F2WCustomLogger.warn('Lock was already released or reassigned', { hook: 'useTicketLock', ticketId });
      } else {
        F2WCustomLogger.error('Failed to release lock', e, { hook: 'useTicketLock', ticketId });
      }
    }
  };

  const stopKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  };

  const startKeepAlive = () => {
    stopKeepAlive();
    keepAliveIntervalRef.current = setInterval(async () => {
      try {
        const response = await FlightTicketApiService.resetLockTimer(ticketId);
        if (!response?.ok || response?.data?.reset === false) {
          throw new Error('The ticket lock is no longer owned by this session');
        }
      } catch (e) {
        F2WCustomLogger.warn('Keep-alive: failed to reset lock timer', {
          hook: 'useTicketLock',
          ticketId,
          error: e?.message,
        });
        stopKeepAlive();
        ownsLockRef.current = false;
        setLockError('The editing lock could not be renewed. Reload the ticket before making further changes.');
        setShowBlockedPopup(true);
      }
    }, KEEP_ALIVE_INTERVAL_MS);
  };

  const startLockTimers = () => {
    reminderTimeoutRef.current = setTimeout(() => setShowReminderPopup(true), SESSION_REMINDER_MS);
    lockTimeoutRef.current = setTimeout(async () => {
      stopKeepAlive();
      await releaseLock();
      window.location.href = '/all';
    }, SESSION_TIMEOUT_MS);
    // Start periodic keep-alive to prevent server-side lock expiration
    startKeepAlive();
  };

  const resetLockTimers = async () => {
    if (!ownsLockRef.current) return false;

    clearTimeout(reminderTimeoutRef.current);
    clearTimeout(lockTimeoutRef.current);
    try {
      const response = await FlightTicketApiService.resetLockTimer(ticketId);
      if (!response?.ok || response?.data?.reset === false) {
        throw new Error('The ticket lock is no longer owned by this session');
      }
    } catch (e) {
      F2WCustomLogger.warn('Failed to reset lock timer', { hook: 'useTicketLock', ticketId, error: e?.message });
      stopKeepAlive();
      ownsLockRef.current = false;
      setLockError('The editing lock could not be renewed. Reload the ticket before making further changes.');
      setShowBlockedPopup(true);
      return false;
    }
    startLockTimers();
    setShowReminderPopup(false);
    return true;
  };

  const acquireLock = async () => {
    try {
      // Try to acquire lock directly
      const response = await FlightTicketApiService.acquireLock(ticketId);
      if (response?.ok) {
        ownsLockRef.current = true;
        setLockInfo({ isLocked: true, lockedBy: user?.email });
        setLockError(null);
        setShowBlockedPopup(false);
        startLockTimers();
      } else {
        throw new Error('The lock endpoint returned an invalid response');
      }
    } catch (error) {
      // If 409 Conflict, ticket is locked by another user
      if (error?.response?.status === 409) {
        // Error structure: error.response.data.details.lock
        const errorData = error?.response?.data;
        const lockData = errorData?.details?.lock || errorData?.data?.lock;
        const lockedByUser = lockData?.lockedBy || 'another user';

        F2WCustomLogger.log('Lock conflict detected', { lockData, lockedByUser, currentUser: user?.email });

        // A lock held in another tab must also block this tab. Otherwise closing
        // either tab releases the shared email-based lock while the other keeps editing.
        const normalizedLockInfo = {
          lock: {
            lockedBy: lockedByUser,
            lockedAt: lockData?.lockedAt,
            expiresAt: lockData?.expiresAt,
          },
          isLocked: true,
        };
        setLockInfo(normalizedLockInfo);
        setShowBlockedPopup(true);
      } else {
        // Fail closed: editing must never continue without a confirmed lock.
        F2WCustomLogger.error('Failed to acquire lock', error, { hook: 'useTicketLock', ticketId });
        setLockError('The editing lock could not be acquired. Reload the ticket or try again later.');
        setShowBlockedPopup(true);
      }
    }
  };

  useEffect(() => {
    if (!isLockRequired) return;
    if (!user?.email) {
      setLockError('Your authenticated profile has no email address, so the editing lock cannot be acquired.');
      setShowBlockedPopup(true);
      return;
    }

    acquireLock();

    // Best-effort release when the tab is being torn down so the next user can
    // enter after a refresh without waiting for the 20-minute server expiry.
    // `beforeunload` alone is unreliable (it often does not fire on mobile or
    // when a backgrounded tab is discarded), so we also listen for `pagehide`,
    // which fires reliably in those cases. Both go through the same keepalive
    // request; releasing twice is harmless (the server treats an already-freed
    // lock as a no-op).
    const releaseLockOnUnload = () => {
      if (!ownsLockRef.current) return;

      // Clear ownership synchronously so the unmount cleanup does not fire a
      // second, redundant release for the same session.
      ownsLockRef.current = false;

      const apiUrl = `${getEnvironmentVariablesHandler().apiBaseUrl}flight-ticket/release-lock/${ticketId}?email=${encodeURIComponent(user?.email)}`;

      fetch(apiUrl, {
        method: 'DELETE',
        keepalive: true,
      }).catch(() => {});
    };

    // Handle visibility change (tab switch, minimize, etc.)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - could add logic here if needed
      } else {
        // Tab is visible again - reset timers to keep session alive
        if (ownsLockRef.current) {
          resetLockTimers();
        }
      }
    };

    window.addEventListener('beforeunload', releaseLockOnUnload);
    window.addEventListener('pagehide', releaseLockOnUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopKeepAlive();
      clearTimeout(lockTimeoutRef.current);
      clearTimeout(reminderTimeoutRef.current);
      window.removeEventListener('beforeunload', releaseLockOnUnload);
      window.removeEventListener('pagehide', releaseLockOnUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseLock();
    };
    // eslint-disable-next-line
  }, [ticketId, user?.email, isLockRequired]);

  return {
    lockInfo,
    lockError,
    isLockRequired,
    isLockPending: isLockRequired && !lockInfo && !showBlockedPopup,
    // True only when this session confirmed ownership of the lock. Used to gate
    // the ticket data fetch/render so a blocked user never loads the profile.
    hasLock: isLockRequired ? Boolean(lockInfo) && !showBlockedPopup : true,
    showBlockedPopup,
    showReminderPopup,
    resetLockTimers,
    setShowReminderPopup,
  };
};
